import { getPath } from "../writeshift/Path";
import { TreeRangeDelta, singularTreeRange } from "../writeshift/Range";

function isAddedTextNode(record: MutationRecord): boolean {
  const result =
    record.type === "childList" &&
    record.addedNodes.length === 1 &&
    record.addedNodes.item(0)?.nodeType === Node.TEXT_NODE;
  return result;
}
function isChangedText(record: MutationRecord): boolean {
  const result = record.type === "characterData";
  return result;
}

function isAddition(record: MutationRecord): boolean {
  const result =
    record.type === "childList" &&
    record.addedNodes.length > 0 &&
    record.removedNodes.length <= 0;
  return result;
}
function isRemoval(record: MutationRecord): boolean {
  const result =
    record.type === "childList" &&
    record.removedNodes.length > 0 &&
    record.addedNodes.length <= 0;
  return result;
}

function getEditType(
  record: MutationRecord
): "Changed" | "Addition" | "Removal" | "Mixed" {
  if (isAddition(record)) {
    return "Addition";
  }
  if (isChangedText(record)) {
    return "Changed";
  }
  if (isRemoval(record)) {
    return "Removal";
  }
  return "Mixed";
}

function areSplitTextNodes(
  newTextNode: MutationRecord,
  changedOriginalText: MutationRecord
): boolean {
  if (!isAddedTextNode(newTextNode)) {
    return false;
  }
  if (!isChangedText(changedOriginalText)) {
    return false;
  }
  const possiblePrefix = newTextNode.addedNodes.item(0)?.textContent;
  if (!possiblePrefix) {
    return false;
  }
  const { oldValue } = changedOriginalText;
  if (oldValue === null) {
    return false;
  }

  const result = oldValue.startsWith(possiblePrefix);
  return result;
}

interface HistoryChildListItem {
  textNode: Node;
  type: "added" | "removed";
  text: null;
}
interface HistoryCharacterDataItem {
  textNode: Node;
  type: "changed";
  text: string;
}
type HistoryItem = HistoryCharacterDataItem | HistoryChildListItem;

export function isOutlookLineChange(records: MutationRecord[]): boolean {
  if (records.length !== 3) {
    return false;
  }
  const [addedText, removedBreak, addCharacterData] = records;

  // Add a line
  const isAddLine =
    isAddedTextNode(addedText) &&
    isRemoval(removedBreak) &&
    addedText.nextSibling === removedBreak.removedNodes[0] &&
    isChangedText(addCharacterData) &&
    addCharacterData.target === addedText.addedNodes[0];

  // or removing one

  const [removeCharacterData, removeText, addBreak] = records;

  const isRemoveLine =
    isChangedText(removeCharacterData) &&
    isRemoval(removeText) &&
    removeCharacterData.target === removeText.removedNodes[0] &&
    isAddition(addBreak);

  return isAddLine || isRemoveLine;
}

function getHistoryItem(record: MutationRecord): HistoryItem | null {
  if (isAddedTextNode(record)) {
    const node = record.addedNodes.item(0);

    return node
      ? {
          textNode: node,
          type: "added",
          text: null,
        }
      : null;
  }
  if (isChangedText(record)) {
    return {
      textNode: record.target,
      type: "changed",
      text: record.oldValue || "",
    };
  }
  if (
    isRemoval(record) &&
    record.removedNodes.item(0)?.nodeType === Node.TEXT_NODE
  ) {
    const node = record.removedNodes.item(0);
    return node
      ? {
          textNode: node,
          type: "removed",
          text: null,
        }
      : null;
  }

  return null;
}

/**
 * DONE
 *  - middle style application, length of 9: <div>Hello world</div> -> <div>Hello <b>wor</b>ld</div>
 *
 * Working on:
 *  - undo middle style application, length of 9: <div>Hello <b>wor</b>ld</div> -> <div>Hello world</div>
 *
 * TODO:
 *  - beginning and end style application, length of 7 <div>Hello world</div> -> <div><b>Hello</b> world</div>
 *  - Nested style application, length of 14 <div>He<b>llo w</b>ordl</div> -> <div>H<i>e</i><b><i>llo</i> w</b>orld</div>
 *
 */

/**
 * @param records Mutation records
 * @returns true iff the records indicate that there is a style split in the middle
 * for example bolding a word in the middle of a sentence
 */
export function isStyleSplit(records: MutationRecord[]): boolean {
  // TODO(compatibility): only in Chrome
  if (records.length !== 9) {
    return false;
  }

  // Prefix, edit, suffix
  // The prefix node should be the original position

  const [
    addLeftMost,
    removeLeftTextFromRight,
    addStyledTextInRange,
    removeStyledTextFromRight,
    addUselessSpan,
    removeUselessSpan,
    addStyleTag,
    removeStyledText,
    addStyledTextToStyleTag,
  ] = records;

  // The original text node is

  return (
    areSplitTextNodes(addLeftMost, removeLeftTextFromRight) &&
    areSplitTextNodes(addStyledTextInRange, removeStyledTextFromRight) &&
    isAddition(addUselessSpan) &&
    isRemoval(removeUselessSpan) &&
    isAddition(addStyleTag) &&
    isRemoval(removeStyledText) &&
    removeStyledText.removedNodes.item(0) ===
      addStyledTextInRange.addedNodes.item(0) &&
    isAddition(addStyledTextToStyleTag) &&
    addStyledTextToStyleTag.target === addStyleTag.addedNodes.item(0)
  );
}

type OldValue = string;
/**
 * The value at index i represents the
 */
type TextNodeHistory = Map<Node, (OldValue | null)[]>;

// Map a node to the history of its textContent
// e.g. textNode -> [null, null, 'value', null, 'newValue', 'finalValue']
function getTextNodeHistory(records: MutationRecord[]): TextNodeHistory {
  const nodeToHistory = new Map<Node, (string | null)[]>();
  for (const [i, record] of Array.from(records.entries())) {
    const historyItem = getHistoryItem(record);
    if (historyItem) {
      const { textNode, text } = historyItem;
      if (nodeToHistory.has(textNode)) {
        // Existing
        const history = nodeToHistory.get(textNode);
        if (history && text) {
          history[i] = text;
        }
      } else {
        // New

        // Empty history, with one more null because we need to set the last value to the current value of the node
        const newHistory: (OldValue | null)[] = [
          ...records.map(() => null),
          null,
        ];
        if (text) {
          newHistory[i] = text;
        }
        nodeToHistory.set(textNode, newHistory);
      }
    }
  }
  // Iterate through all the text nodes and get their current values to be up-to-date
  // This is what is sorely missing from mutation observer api...
  for (const [node, history] of Array.from(nodeToHistory.entries())) {
    history[history.length - 1] = node.textContent;
  }

  return nodeToHistory;
}

interface IndexedRecord {
  record: MutationRecord;
  index: number;
}

/**
 * Get the value of the text node at the point in history in the mutation record batch.
 */
function textValueAt(
  index: number,
  node: Node,
  history: TextNodeHistory
): string | null {
  if (!history.has(node)) {
    return null;
  }
  const textHistory = history.get(node);
  if (!textHistory) {
    return null;
  }
  return textHistory.slice(index + 1).find((h) => h != null) || null;
}

function isMerge(
  { record: textChange, index: changeIndex }: IndexedRecord,
  { record: removal, index: removalIndex }: IndexedRecord,
  history: TextNodeHistory
): boolean {
  if (!isChangedText(textChange)) {
    return false;
  }
  if (!isRemoval(removal)) {
    return false;
  }
  const changeNode = getHistoryItem(textChange)?.textNode;
  const removalNode = getHistoryItem(removal)?.textNode;
  if (!changeNode || !removalNode) {
    return false;
  }

  const changeText = textValueAt(changeIndex, changeNode, history);
  const removalText = textValueAt(removalIndex, removalNode, history);
  if (!changeText || !removalText) {
    return false;
  }

  return changeText.startsWith(removalText);
}

export function isStyleSplitUndo(records: MutationRecord[]): boolean {
  if (records.length !== 9) {
    return false;
  }
  const textNodeToHistory = getTextNodeHistory(records);

  const [
    removeStyledTextNode, // 0
    addStyledTextNodeBeforeRestOfString, // 1
    removeStyleNode, // 2
    addUselessSpan, // 3
    removeUselessSpan, // 4
    addStyledTextNodeToFinalTextNode, // 5
    removedStyledTextNode, // 6
    addPrefixTextNodeToFinalTextNode, // 7
    removePrefixNode, // 8
  ] = records;
  return (
    isRemoval(removeStyledTextNode) &&
    isAddedTextNode(addStyledTextNodeBeforeRestOfString) &&
    isRemoval(removeStyleNode) &&
    isAddition(addUselessSpan) &&
    isRemoval(removeUselessSpan) &&
    isMerge(
      { record: addStyledTextNodeToFinalTextNode, index: 5 },
      { record: removedStyledTextNode, index: 6 },
      textNodeToHistory
    ) &&
    isMerge(
      { record: addPrefixTextNodeToFinalTextNode, index: 7 },
      { record: removePrefixNode, index: 8 },
      textNodeToHistory
    )
  );
}

/**
 * Gets the ranges that need shifting based on a styled text edit in Chrome (e.g. pressing Ctrl+B in a contenteditable)
 *
 * Original: "Some text node" -> at some path, X
 *
 *               | bold the word "text"
 *               v
 *
 * New: "Some " <b> " node" -> The "Some " is still at path X, but the " node" is at X[X.length - 1] += 2
 *            "text"
 *
 * The changes would be to remove the alerts at "text", and shift over the alerts in " node" by
 * subtracting the length of "Some " and "text".
 *
 */
export function getStyleSplitRangeDeltas(
  root: Node,
  records: MutationRecord[]
): TreeRangeDelta[] | null {
  if (!isStyleSplit(records)) {
    return null;
  }

  const nodeAtOriginalTextNodePosition = records[0].addedNodes.item(0);
  if (!nodeAtOriginalTextNodePosition) {
    return null;
  }

  const originalPath = getPath({
    root,
    target: nodeAtOriginalTextNodePosition,
  });
  if (!originalPath) {
    return null;
  }

  const r = new Range();
  r.selectNodeContents(nodeAtOriginalTextNodePosition);
  const prefixLength = r.endOffset;

  // The bolded or italicized node, etc
  const styledTextNode = records[2].addedNodes.item(0);
  if (!styledTextNode) {
    return null;
  }
  r.selectNodeContents(styledTextNode);
  const styledLength = r.endOffset;

  r.selectNodeContents(nodeAtOriginalTextNodePosition);
  const suffixLength = r.endOffset;

  return [
    {
      range: singularTreeRange(
        originalPath,
        prefixLength,
        prefixLength + styledLength
      ),
      delta: null, // remove
    },
    {
      range: singularTreeRange(originalPath, styledLength, suffixLength),
      delta: {
        lastIndex: 2,
        offset: -1 * (prefixLength + styledLength), // subtract the prefix and styled nodes' length
      },
    },
  ];
}

export function getStyleSplitUndoRangeDeltas(
  root: Node,
  records: MutationRecord[]
): TreeRangeDelta[] | null {
  if (!isStyleSplitUndo(records)) {
    return null;
  }

  // The first path, prefix, is retained
  // The second path, styled, is removed
  // The third path, suffix, is shifted into the first range

  // This 5 not magic because we know its type based on isStyleSplitUndo
  const finalTextNode = records[5].target;

  /**
   * Old paths
   *              [R]
   *   /     /     |        \      \
   *  ... [R, i] [R, i+1] [R, i+2] ...
   *  prefix^      |         ^suffix
   *           [R, i+1, 0]
   *                ^ styled, this is the text node that used to be bolded or italized, or whatever
   *
   * New paths
   *             [R]
   *         /    |    \
   *       ...  [R, i] ...
   *              ^this is the new, merged text node's path..
   *
   * All the underlines in styled have to be removed
   * All the underlines in suffix have to be moved into the new path
   */

  // prefix
  const originalFirstPath = getPath({ root, target: finalTextNode }); // [R, i]
  if (originalFirstPath === null) {
    return null;
  }

  // styled
  const oldStyledPath = [...originalFirstPath];
  oldStyledPath[oldStyledPath.length - 1] += 1; // [R, i+1]
  oldStyledPath.push(0); // [R, i+1, 0]

  // suffix
  const oldSuffixPath = [...originalFirstPath];
  oldSuffixPath[oldSuffixPath.length - 1] += 2; // [R, i+2]

  const removeStyledRange = {
    delta: null, // remove
    range: singularTreeRange(oldStyledPath, 0, Infinity),
  };

  const prefixLength = records[8].removedNodes.item(0)?.textContent?.length;
  const styledLength = records[6].removedNodes.item(0)?.textContent?.length;
  if (prefixLength === undefined || styledLength === undefined) {
    return null;
  }

  const shiftSuffixRanges = {
    range: singularTreeRange(oldSuffixPath, 0, Infinity),
    delta: {
      offset: -2,
      lastIndex: prefixLength + styledLength,
    },
  };

  return [removeStyledRange, shiftSuffixRanges];
}

// If a prefix or suffix range of a text node is styled (made bold or italicized or whatever)
// this function returns which side it was, or null if it isn't
export function getBoundaryStyleSide(
  records: MutationRecord[]
): "LEFT" | "RIGHT" | null {
  if (records.length !== 7) {
    return null;
  }
  const [
    addPrefixTextNode, // 0
    changeRightText, // 1
    addSpan, // 2
    removeSpan, // 3
    addStyleNode, // 4
    removeStyledTextNode, // 5
    addStyledTextNode, // 6
  ] = records;

  if (
    !(
      isAddedTextNode(addPrefixTextNode) &&
      isChangedText(changeRightText) &&
      isAddition(addSpan) &&
      isRemoval(removeSpan) &&
      isAddition(addStyleNode) &&
      isRemoval(removeStyledTextNode) &&
      isAddedTextNode(addStyledTextNode)
    )
  ) {
    return null;
  }

  const styleNode = addStyleNode.addedNodes.item(0);
  if (!styleNode) {
    return null;
  }

  const maybeStyledTextNode = changeRightText.target;

  if (styleNode.firstChild === maybeStyledTextNode) {
    return "RIGHT";
  }

  return "LEFT";
}

export function getBoundaryStyleDeltas(
  root: Node,
  records: MutationRecord[]
): TreeRangeDelta[] | null {
  const side = getBoundaryStyleSide(records);
  if (!side) {
    return null;
  }
  const leftTextNode = records[0].addedNodes.item(0);
  if (!leftTextNode) {
    return null;
  }
  const originalPath = getPath({ root, target: leftTextNode });
  if (!originalPath) {
    return null;
  }
  const leftTextNodeLength = leftTextNode.textContent?.length;
  const rightStyledTextNodeLength = records[1].target.textContent?.length;

  if (!rightStyledTextNodeLength || !leftTextNodeLength) {
    return null;
  }

  if (side === "RIGHT") {
    const startOffset = leftTextNodeLength;
    const endOffset = leftTextNodeLength + rightStyledTextNodeLength;
    const removeInStyled: TreeRangeDelta = {
      delta: null,
      range: singularTreeRange(originalPath, startOffset, endOffset),
    };

    // We don't need to shift anything because the edits happened on the right
    return [removeInStyled];
  }

  // side === "LEFT"

  // anything in the edit should be removed
  const removeInStyled: TreeRangeDelta = {
    delta: null,
    range: singularTreeRange(originalPath, 0, leftTextNodeLength),
  };

  const shift: TreeRangeDelta = {
    delta: {
      lastIndex: 1,
      offset: -leftTextNodeLength,
    },
    range: singularTreeRange(originalPath, leftTextNodeLength, Infinity),
  };

  return [removeInStyled, shift];
}

export function isHomogenous(records: MutationRecord[]): boolean {
  if (records.length <= 0) {
    return true;
  }
  const firstType = getEditType(records[0]);

  for (const record of records) {
    const type = getEditType(record);
    if (type !== firstType || type === "Mixed") {
      return false;
    }
  }

  return true;
}
